import { useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router'
import { useFaq, useReferralCode } from 'content/api'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import config from 'constants/config'
import { connect } from 'react-redux'
import HeadData from 'components/Common/HeadData'
import HeroImage from 'content/components/HeroImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LoggedInOnly from 'components/Common/LoggedInOnly'
import styled from 'styled-components'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'
import { copyValueToClipboard } from 'lib/web/clipboardUtils'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import type { KeyMessage } from 'content/components/KeyMessages'
import KeyMessages from 'content/components/KeyMessages'
import PageSection from 'content/components/PageSection'
import LineLinkIcon from 'LuxKit/Icons/line/LineLinkIcon'
import TextLink from 'LuxKit/TextLink'
import OfferList from 'components/OfferList/OfferList'
import FrequentlyAskedQuestions from 'components/Content/FrequentlyAskedQuestions'
import FormatCurrency from 'components/Common/FormatCurrency'
import type { FetchAndDispatchFunction } from 'src/server/state/types'
import { getReferralCodeValidation } from 'api/promo'
import { getLegalPage } from 'api/content'
import { getMaxRefereeProductTypeDiscount } from 'marketing/shared/getMaxRefereeProductTypeDiscount'
import { fetchReferralValue } from 'actions/ReferAction'
import { FLASH_OFFERS_TYPES } from 'constants/offerList'
import ReferralTopLocations from './ReferralTopLocations'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getReferrerNameFromReferralCode } from 'marketing/shared/getReferrerNameFromReferralCode'

const CodeBox = styled(Group)`
  width: ${rem(400)};
  max-width: 100%;
  padding: ${rem(8)};
  margin: 0 auto;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  background: ${props => props.theme.palette.neutral.default.eight};
`

const CenteredContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const CenteredVerticalSpacer = styled(VerticalSpacer)`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const EscapeWithUsText = styled(BodyText)`
  max-width: ${rem(948)};
`

const Wrapper = styled.div`
  z-index: ${zIndex.min};
  width: 100%;
  max-width: ${rem(860)};
`

interface RouteProps {
  referralCode: string
}

interface Props extends RouteComponentProps<RouteProps> {
  memberId: string
  numberOfPurchases: number
  regionCode: string
  maxRefereeAmount: number | null
}

function RefereeLanding(props: Props) {
  const { referralCode } = props.match.params
  const { memberId, numberOfPurchases, maxRefereeAmount, regionCode } = props

  const { isSuccess } = useReferralCode(referralCode)
  const { data: faq } = useFaq('referrer-program-faq')

  const referrerName = useMemo(() => getReferrerNameFromReferralCode({ referralCode }), [referralCode])

  const dispatch = useAppDispatch()
  const intl = useIntl()

  const howItWorks: Array<KeyMessage> = useMemo(() => [{
    icon: 'star',
    title: intl.formatMessage({
      id: 'yqyzKTCIl6hJfWAEdl3w',
      defaultMessage: 'Become a member',
      description: 'How it works step 1 title on referee landing page in the content section',
    }),
    text: intl.formatMessage({
      id: 'qfapNJXzS5uf1ODXD7Sf',
      defaultMessage: 'Access exclusive offers at membership prices.',
      description: 'How it works step 1 description on referee landing page',
    }),
  }, {
    icon: 'search',
    title: intl.formatMessage({
      id: '_t41bo21EIB8_yF9gKuK',
      defaultMessage: 'Find your perfect escape',
      description: 'How it works step 2 title on referee landing page in the content section',
    }),
    text: intl.formatMessage({
      id: 'k3kDRL_0cNIuGqXFYajC',
      defaultMessage: 'From beach holidays in Bali to European city breaks.',
      description: 'How it works step 2 description on referee landing page',
    }),
  }, {
    icon: 'mountains-sun',
    title: intl.formatMessage({
      id: 'hG8uB3Gw_7LTTnk-9sQS',
      defaultMessage: 'Book your first escape',
      description: 'How it works step 3 title on referee landing page in the content section',
    }),
    text: intl.formatMessage({
      id: 'vt-r1zHl1g9014TSz7l2',
      defaultMessage: 'Enter the unique referal promo code at checkout.',
      description: 'How it works step 3 description on referee landing page',
    }),
  }], [intl])

  const copiedLabel = intl.formatMessage({
    id: 'BFbSHY3U2x9h2v_jbhgc',
    defaultMessage: 'Copied!',
    description: 'Button text shown after referral code is copied to clipboard on referee landing page',
  })
  const copyLabel = intl.formatMessage({
    id: '-cAE0W3eMR_WSwUrC-Zf',
    defaultMessage: 'Copy',
    description: 'Button text to copy referral code to clipboard on referee landing page',
  })
  const becomeMemberLabel = intl.formatMessage({
    id: 'VmzGu5_1fs6YOd-JLJG5',
    defaultMessage: 'Become a member to claim',
    description: 'Button text prompting non-members to sign up on referee landing page',
  })
  const browseAllOffersLabel = intl.formatMessage({
    id: 'YlILRIryc8m6IblvOcBH',
    defaultMessage: 'Browse all offers',
    description: 'Button text to navigate to all offers from referee landing page',
  })
  const itsEasyHeading = intl.formatMessage({
    id: 'gPVUsjrpeyN88WjFnFWw',
    defaultMessage: 'It’s easy to get started',
    description: 'Heading for the how-it-works section on referee landing page',
  })
  const simplyUseReferralBody = intl.formatMessage({
    id: 'X04ibUzLoxP4sKyxZAaT',
    defaultMessage: 'Simply use your friends referral at checkout and start packing your suitcase. It’s that simple.',
    description: 'Description text explaining how to use a referral code on referee landing page',
  })

  useEffect(() => {
    dispatch(fetchReferralValue(regionCode))
  }, [dispatch, regionCode])

  const [hasCopied, setCopied] = useState<boolean>(false)

  if (!isSuccess) {
    // @TODO: error state for referee page
    <Error404PageWithOffers />
  }

  return (
    <>
      <HeadData
        title={intl.formatMessage({
          id: 'ASvPMZsDQ80wZCkmHJS4',
          defaultMessage: 'Save money on your first Luxury Escape',
          description: 'Page title for the referee landing page in the content section',
        })}
        description={intl.formatMessage({
          id: 'MQNm58K70XvNqAaBlh3C',
          defaultMessage: 'Let Luxury Escapes open up a world of amazing destinations, incredible inclusions and unforgettable experiences',
          description: 'HTML meta description for the referee landing page',
        })}
        imageId="nyaamiuv8bnw1ww4avza2xqifyle"
      />
      <HeroImage
        alt={intl.formatMessage({
          id: 'rnn9iHLKy9GHVyu7OdpJ',
          defaultMessage: 'Beautiful places to visit',
          description: 'Accessibility alt text for hero image on referee landing page',
        })}
        aria-hidden="true"
        id="gltlms76tqshg8a6agu"
      >
        <Wrapper>
          <VerticalSpacer gap={numberOfPurchases > 0 ? 32 : 16}>
            <VerticalSpacer gap={12}>
              {!memberId && (
                <Heading variant="heading2" as="h1" colour="neutral-eight" align="center" format="propercase">
                  {intl.formatMessage(
                    {
                      id: 'LrhvPQ2tyQRJYo8JIa3q',
                      defaultMessage: '{referrerName} just gave you the ultimate perk!',
                      description: 'Heading when non-member lands on referee landing page, showing referrer name',
                    },
                    { referrerName },
                  )}
                </Heading>
              )}
              {memberId && numberOfPurchases === 0 && maxRefereeAmount && (
                <Heading variant="heading2" as="h1" colour="neutral-eight" align="center">
                  <FormattedMessage
                    id="FkXwR4NS1jjspd89CJCP"
                    defaultMessage="Save up to {amount} off your first {holiday}"
                    description="Heading showing maximum referral discount for first-time bookers on referee landing page"
                    values={{
                      amount: <FormatCurrency value={maxRefereeAmount} />,
                      holiday: config.brandConfig.brandHoliday,
                    }}
                  />
                </Heading>
              )}
              {memberId && numberOfPurchases > 0 && (
                <Heading variant="heading2" as="h1" colour="neutral-eight" align="center">
                  {intl.formatMessage(
                    {
                      id: 'Gqn6ptt0RS2_mURtoz1D',
                      defaultMessage: 'Looks like you’ve booked an {holiday} before!',
                      description: 'Heading shown to returning customers on referee landing page',
                    },
                    { holiday: config.brandConfig.holidaySynonym },
                  )}
                </Heading>
              )}
              {!memberId && maxRefereeAmount && (
                <BodyText variant="large" colour="neutral-eight" align="center">
                  <FormattedMessage
                    id="KM0PzNwoNw5qW0UL18hU"
                    defaultMessage="Save up to {amount} off your first {holiday}"
                    description="Body text showing maximum referral discount for non-members on referee landing page"
                    values={{
                      amount: <FormatCurrency value={maxRefereeAmount} />,
                      holiday: config.brandConfig.brandHoliday,
                    }}
                  />
                </BodyText>
              )}
              {memberId && maxRefereeAmount && numberOfPurchases === 0 && (
                <BodyText variant="large" colour="neutral-eight" align="center">
                  <FormattedMessage
                    id="cjiIqA9AZmkyDZ2kwUL1"
                    defaultMessage="Simple copy the code below for up to {amount}"
                    description="Instructions to copy referral code with discount amount on referee landing page"
                    values={{
                      amount: <FormatCurrency value={maxRefereeAmount} />,
                    }}
                  />
                </BodyText>
              )}
              {memberId && numberOfPurchases > 0 && (
                <BodyText variant="large" colour="neutral-eight" align="center">
                  {intl.formatMessage({
                    id: 'oQivVzE0gWKdCZf9hCGv',
                    defaultMessage: 'Referral rewards can only be claimed for first time bookings. Start referring your friends to earn more rewards.',
                    description: 'Message shown to returning customers explaining referral limitations on referee landing page',
                  })}
                </BodyText>
              )}
            </VerticalSpacer>
            <CenteredContent>
              <LoggedInOnly buttonText={becomeMemberLabel} buttonProps={{ size: 'large' }}>
                {numberOfPurchases === 0 && (
                  <VerticalSpacer gap={16}>
                    <Heading variant="heading6" colour="neutral-eight" align="center">
                      {intl.formatMessage(
                        {
                          id: 'R1EpjdCh_Gx1lkJM26LF',
                          defaultMessage: "{referrerName}'s Promo Code",
                          description: 'Heading showing the referrer name with their promo code on referee landing page',
                        },
                        { referrerName },
                      )}
                    </Heading>
                    <VerticalSpacer gap={32}>
                      <CodeBox direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
                        <BodyText variant="medium" wrap="truncate">
                          {referralCode}
                        </BodyText>
                        <TextButton
                          kind="tertiary"
                          size="large"
                          startIcon={!hasCopied ? <LineLinkIcon size="S" /> : undefined}
                          onClick={() => {
                            copyValueToClipboard(referralCode)
                            setCopied(true)
                            window.setTimeout(() => {
                              setCopied(false)
                            }, 1500)
                          }}
                        >
                          {hasCopied ? copiedLabel : copyLabel}
                        </TextButton>
                      </CodeBox>
                      <TextButton kind="primary" size="large" fit="auto" to="/">
                        {browseAllOffersLabel}
                      </TextButton>
                    </VerticalSpacer>
                  </VerticalSpacer>
                )}
                {numberOfPurchases > 0 && (
                  <TextButton kind="primary" size="large" fit="auto" to="/refer">
                    {intl.formatMessage({
                      id: 'LYVLZxs-J6VfpBqTA_sd',
                      defaultMessage: 'How to refer a friend',
                      description: 'Button text for returning customers to navigate to referral page',
                    })}
                  </TextButton>
                )}
              </LoggedInOnly>
            </CenteredContent>
          </VerticalSpacer>
        </Wrapper>
      </HeroImage>
      {!memberId && (
        <PageSection>
          <CenteredVerticalSpacer gap={16}>
            <Heading variant="heading3" align="center">
              {itsEasyHeading}
            </Heading>
            <BodyText variant="large" align="center">
              {simplyUseReferralBody}
            </BodyText>
            <KeyMessages items={howItWorks} />
            <BodyText variant="large">
              <FormattedMessage
                id="-nE0XpZ5PBsN-dA8G3pG"
                defaultMessage="Read our full <link>Terms & Conditions</link>"
                description="Link to terms and conditions on referee landing page"
                values={{
                  link: (chunks) => <TextLink weight="regular" to="/promotions#referral-program">{chunks}</TextLink>,
                }}
              />
            </BodyText>
          </CenteredVerticalSpacer>
        </PageSection>
      )}

      <PageSection>
        <CenteredVerticalSpacer gap={40}>
          <Heading variant="heading3">
            {intl.formatMessage({
              id: 'NRuDT8D_C3zNc2L6CT4p',
              defaultMessage: 'Our handpicked escapes',
              description: 'Heading for the curated offers section on referee landing page',
            })}
          </Heading>
          <OfferList
            maxOffers={3}
            filters={{
              offerTypes: FLASH_OFFERS_TYPES,
              frontPageVisibleOnly: true,
            }}
          />
          <TextButton kind="primary" size="large" to="/">
            {browseAllOffersLabel}
          </TextButton>
        </CenteredVerticalSpacer>
      </PageSection>

      {memberId && (
        <PageSection>
          <CenteredVerticalSpacer gap={16}>
            <Heading variant="heading3" align="center">
              {itsEasyHeading}
            </Heading>
            <BodyText variant="large" align="center">
              {simplyUseReferralBody}
            </BodyText>
            <KeyMessages items={howItWorks} />
            <BodyText variant="medium">
              <FormattedMessage
                id="b5Z1ow6191vtjgnxCltq"
                defaultMessage="Read our full <link>Terms & Conditions</link>"
                description="Link to terms and conditions for members on referee landing page"
                values={{
                  link: (chunks) => <TextLink weight="regular" to="/promotions#referral-program">{chunks}</TextLink>,
                }}
              />
            </BodyText>
          </CenteredVerticalSpacer>
        </PageSection>
      )}

      <PageSection>
        <CenteredVerticalSpacer gap={40}>
          <Heading variant="heading3" align="center">
            {intl.formatMessage({
              id: 'wltxbA_Y52HwpStFgqB2',
              defaultMessage: 'Escape with us...',
              description: 'Heading for the destinations section on referee landing page',
            })}
          </Heading>
          <EscapeWithUsText variant="large" align="center">
            {intl.formatMessage(
              {
                id: 'lQz5UldTHKtgGy7yjXI2',
                defaultMessage: 'Let {brandName} open up a world of amazing destinations, incredible inclusions and unforgettable experiences. Simply choose a country below and start turning your holiday dreams into reality...',
                description: 'Description text encouraging users to explore destinations on referee landing page',
              },
              { brandName: 'Luxury Escapes' },
            )}
          </EscapeWithUsText>
          <ReferralTopLocations />
          <TextButton kind="primary" size="large" to="/">
            {intl.formatMessage({
              id: 'osh3B2-gXEeP6fQDHAjN',
              defaultMessage: 'See more destinations',
              description: 'Button text to view more destinations on referee landing page',
            })}
          </TextButton>
        </CenteredVerticalSpacer>
      </PageSection>

      {faq && (
        <PageSection>
          <FrequentlyAskedQuestions
            heading={intl.formatMessage({
              id: 'xNLrb5fQ94073lo0pSD4',
              defaultMessage: 'Frequently asked questions',
              description: 'Heading for the FAQ section on referee landing page',
            })}
            items={faq}
            maxToShow={10}
          />
        </PageSection>
      )}
    </>
  )
}

const mapState = (state: App.State) => {
  return {
    memberId: state.auth.account.memberId,
    regionCode: state.geo.currentRegionCode,
    maxRefereeAmount: state?.refer?.referralValue?.product_type_discount ? getMaxRefereeProductTypeDiscount(state.refer.referralValue.product_type_discount) : null,
    // WTF SVC-AUTH RETURNS NULL
    numberOfPurchases: state.auth.account.numberOfPurchases || 0,
  }
}

export default withRouter(connect(mapState)(RefereeLanding))

const REFEREE_LEGAL_PAGE_SLUG = 'referrer-program-faq'

export const prefetch: FetchAndDispatchFunction<RouteProps> = async({
  match,
  queryClient,
}) => {
  await queryClient.prefetchQuery(['referralCode', match.params.referralCode], () =>
    getReferralCodeValidation(match.params.referralCode),
  )
  await queryClient.prefetchQuery(['faq', REFEREE_LEGAL_PAGE_SLUG], () =>
    getLegalPage(REFEREE_LEGAL_PAGE_SLUG),
  )
}
